import pdfMake from 'pdfmake/build/pdfmake';
import JsBarcode from 'jsbarcode';
import { IStickerPdf } from '../../../../services/model/IStickerPdf';
import { logo2025Img } from '../../../../services/others/assets';

/**
 * Outsource Product Sticker PDF Generator
 * 外采产品标签 - logoType 3 (Without made in NZ)
 */
export default async (data: IStickerPdf[], required: string) => {
  const vfsFonts = await import("pdfmake/build/vfs_fonts");
  const { vfs } = vfsFonts.pdfMake;
  pdfMake.vfs = vfs;

  const documentDefinition: any = {
    pageSize: {
      width: 283,  // 100mm
      height: 198  // 70mm
    },
    content: generateContent(data),
    pageMargins: [15, 15, 15, 15],  // 增大边距
  };

  if (required === 'print') {
    pdfMake.createPdf(documentDefinition).print({});
  }
  if (required === 'view') {
    pdfMake.createPdf(documentDefinition).open();
  }
};

// 生成条形码 base64
const textToBase64Barcode = (text: string) => {
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, text, {
    format: "CODE128",
    width: 2,
    height: 45,  // 条形码高度缩小到 70% (50 * 0.7 = 35)
    displayValue: false,
    margin: 0
  });
  return canvas.toDataURL('image/png');
};

// 获取当前日期
const getCurrentDate = () => {
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yyyy = today.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

// 格式化日期
const formatDate = (dateString: string): string => {
  const date = new Date(dateString + '.000Z');
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

// 生成 PDF 内容
const generateContent = (data: IStickerPdf[]) => {
  const content: any[] = [];

  data.forEach((item) => {
    // ==================== A. 顶部区域 (Header Section) ====================
    // Logo + Product Code + Date 在同一行
    content.push({
      table: {
        widths: [45, '*'],
        body: [[
          // Logo (左侧)
          {
            image: logo2025Img,
            width: 55,
            alignment: 'left',
            margin: [0, 0, 0, 0]
          },
          // Product Code + Date (右侧区域内居中)
          {
            stack: [
              {
                text: item.custProductCode?.toUpperCase() || '',
                fontSize: (item.custProductCode?.length || 0) > 19 ? 13 : 14,
                bold: true,
                alignment: 'left',
                margin: [33, 0, 0, 2]
              },
              {
                text: [
                  { text: 'Date: ', fontSize: 12 },
                  { text: getCurrentDate(), fontSize: 12, bold: true }
                ],
                alignment: 'left',
                margin: [33, 1, 0, 0]
              }
            ],
            margin: [0, 0, 0, 0]
          }
        ]]
      },
      layout: 'noBorders',
      margin: [0, 0, 0, 2]
    });

    // 分割线 (黑色实线, 1pt)
    content.push({
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 253,  // 283 - 15 - 15 (page width - margins)
          y2: 0,
          lineWidth: 1,
          lineColor: 'black'
        }
      ],
      margin: [0, 0, 0, 1]
    });

    // ==================== B. 产品信息区域 (Product Details) ====================
    // Product Name 标签
    content.push({
      text: 'Product Name:',
      fontSize: 11,
      bold: true,
      alignment: 'left',
      margin: [0, 2, 0, 1]
    });

    // Product Name 内容 (自动换行, 左对齐)
    content.push({
      text: item.productName || '',
      fontSize: 11,
      alignment: 'left',
      margin: [0, 0, 0, 3]
    });

    // ==================== C. 条码区域 (Barcode Section) ====================
    // 条形码 (居中)
    content.push({
      image: textToBase64Barcode(item.barcode),
      width: 200,
      alignment: 'center',
      margin: [0, 2, 0, 1]
    });

    // 条码数字 (Human Readable Text)
    content.push({
      text: item.barcode,
      fontSize: 12,
      alignment: 'center',
      margin: [0, 3, 0, 0]
    });

    // 页面分隔（除了最后一项）
    if (data.indexOf(item) !== data.length - 1) {
      content.push({ text: '', pageBreak: 'after' });
    }
  });

  return content;
};
